import { useEffect, useState, useRef } from "react";
import { DateRangePicker } from "react-date-range";
import { FaCalendar } from "react-icons/fa6";
import { useSelector } from "react-redux";

const styles = {
  mainContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
  },

  container: {
    display: "flex",
    alignItems: "center",
    position: "relative",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: "8px",
  },

  filterGroup: {
    display: "flex",
    flexWrap: "wrap",
    // gap: "8px",
    justifyContent: "flex-end",
  },

  filterButton: {
    padding: "6px 8px",
    background: "#f3f4f6",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: "12px",
    border: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  },

  activeFilter: {
    background: "#00C389",
    color: "#fff",
    border: "1px solid #00C389",
  },

  pickerWrapper: {
    position: "absolute",
    top: "50px",
    right: 0,
    zIndex: 1,
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
    overflow: "hidden",
  },

  dateHeader: {
    display: "flex",
    gap: "12px",
    padding: "16px 16px 0px 16px",
    background: "#fff",
    flexWrap: "wrap",
  },

  dateBoxWrapper: {
    flex: 1,
    minWidth: "140px",
  },

  dateLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#6b7280",
    marginBottom: "6px",
  },

  dateBox: {
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    padding: "10px 12px",
    fontSize: "14px",
    fontWeight: 500,
    background: "#f9fafb",
  },

  actionBox: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "12px",
    borderTop: "1px solid #e5e7eb",
    flexWrap: "wrap",
  },

  button: {
    padding: "8px 14px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 600,
  },

  applyButton: {
    background: "#00C389",
    color: "#fff",
  },

  clearButton: {
    background: "#f3f4f6",
  },

  dateText: {
    marginTop: "10px",
    fontSize: "18px",
    fontWeight: 500,
  },

  selectedDate: {
    color: "#00C389",
    fontWeight: 700,
    marginLeft: "5px",
  },

  // NEW: mobile wrapper only (does NOT affect desktop)
  mobileWrapper: {
    width: "100%",
  },

  mobileContainer: {
    justifyContent: "flex-start",
  },

  mobileFilterGroup: {
    justifyContent: "flex-start",
    width: "100%",
  },
};

const DateFilterDashboard = ({ onDateChange }) => {
  const [selectedFilter, setSelectedFilter] = useState("Today");
  const [showPicker, setShowPicker] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const pickerRef = useRef(null);
  const [activeInput, setActiveInput] = useState("start");
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const role = useSelector((store) => store.userReducer.role);

  const getTodayRange = () => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);

    return { startDate, endDate };
  };

  const [state, setState] = useState({
    selection: {
      ...getTodayRange(),
      key: "selection",
    },
  });

  useEffect(() => {
    const now = new Date();

    // max = today (no future dates)
    const max = new Date();
    max.setHours(23, 59, 59, 999);

    // min = 3 months back
    const min = new Date();
    min.setMonth(min.getMonth() - 8);
    min.setHours(0, 0, 0, 0);

    setMinDate(min);
    setMaxDate(max);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPicker]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const { startDate, endDate } = getTodayRange();
    onDateChange(startDate.toISOString(), endDate.toISOString());
  }, []);

  const handleSelect = (ranges) => {
    const selection = ranges?.selection;
    if (!selection?.startDate || !selection?.endDate) return;

    setState({
      selection: {
        startDate: selection.startDate,
        endDate: selection.endDate,
        key: "selection",
      },
    });

    // detect selection phase
    if (!selection.startDate || selection.endDate === selection.startDate) {
      setActiveInput("start");
    } else {
      setActiveInput("end");
    }

    setSelectedFilter("");
  };

  const applyDateFilter = (type) => {
    let start = new Date();
    let end = new Date();

    if (type === "Today") {
      const r = getTodayRange();
      start = r.startDate;
      end = r.endDate;
    }

    if (type === "Yesterday") {
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    }

    if (type === "Week") {
      start = new Date();
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    }

    if (type === "Month") {
      start = new Date();
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end = new Date();
      end.setHours(23, 59, 59, 999);
    }
    if (type === "From Beginning") {
      start = new Date("2020-01-01T00:00:00.000Z");
      start.setHours(0, 0, 0, 0);

      end = new Date();
      end.setHours(23, 59, 59, 999);
    }

    setState({
      selection: {
        startDate: start,
        endDate: end,
        key: "selection",
      },
    });

    onDateChange(start.toISOString(), end.toISOString());
    setSelectedFilter(type);
  };

  const handleApply = () => {
    const { startDate, endDate } = state.selection;

    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    onDateChange(startDate.toISOString(), end.toISOString());
    setShowPicker(false);
    setSelectedFilter("");
  };

  const handleClear = () => {
    applyDateFilter("Today");
    setShowPicker(false);
  };

  return (
    <div
      style={{
        ...styles.mainContainer,
        ...(isMobile ? styles.mobileWrapper : {}),
      }}
    >
      {/* LEFT */}
      <div style={{ width: isMobile ? "100%" : "auto" }}>
        <p style={styles.dateText}>
          Selected Date:
          {state?.selection?.startDate && state?.selection?.endDate && (
            <span style={styles.selectedDate}>
              {state.selection.startDate.toLocaleDateString("en-GB")} -{" "}
              {state.selection.endDate.toLocaleDateString("en-GB")}
            </span>
          )}
        </p>
      </div>

      {/* RIGHT */}
      <div
        style={{
          ...styles.container,
          ...(isMobile ? styles.mobileContainer : {}),
        }}
      >
        <div
          data-tour="date-filter"
          style={{
            ...styles.filterGroup,
            ...(isMobile ? styles.mobileFilterGroup : {}),
          }}
        >
          {[
            "Today",
            "Yesterday",
            "Week",
            "Month",
            ...(["admin", "mops", "subadmin"].includes(role?.toLowerCase())
              ? ["From Beginning"]
              : []),
          ].map((f) => (
            <div
              key={f}
              onClick={() => applyDateFilter(f)}
              style={{
                ...styles.filterButton,
                ...(selectedFilter === f ? styles.activeFilter : {}),
              }}
            >
              {f === "Week" ? "Last 7 Days" : f === "Month" ? "This Month" : f}
            </div>
          ))}
          <div
            onClick={(e) => {
              e.stopPropagation();
              setShowPicker((prev) => !prev);
            }}
            style={{
              ...styles.filterButton,
              ...(showPicker ? styles.activeFilter : {}),
            }}
          >
            <FaCalendar />
          </div>
        </div>

        {showPicker && (
          <div ref={pickerRef} style={styles.pickerWrapper}>
            <div style={styles.dateHeader}>
              <div style={styles.dateBoxWrapper}>
                <p style={styles.dateLabel}>Start Date</p>
                <div
                  style={{
                    ...styles.dateBox,
                    border:
                      activeInput === "start"
                        ? "2px solid #00C389"
                        : "1px solid #d1d5db",
                    background: activeInput === "start" ? "#E6F9F3" : "#f9fafb",
                  }}
                >
                  {state.selection.startDate.toLocaleDateString("en-GB")}
                </div>
              </div>

              <div style={styles.dateBoxWrapper}>
                <p style={styles.dateLabel}>End Date</p>
                <div
                  style={{
                    ...styles.dateBox,
                    border:
                      activeInput === "end"
                        ? "2px solid #00C389"
                        : "1px solid #d1d5db",
                    background: activeInput === "end" ? "#E6F9F3" : "#f9fafb",
                  }}
                >
                  {state.selection.endDate.toLocaleDateString("en-GB")}
                </div>
              </div>
            </div>

            <DateRangePicker
              ranges={[state.selection]}
              onChange={handleSelect}
              showDateDisplay={false}
              minDate={minDate}
              maxDate={maxDate}
            />

            <div style={styles.actionBox}>
              <button
                style={{ ...styles.button, ...styles.applyButton }}
                onClick={handleApply}
              >
                Apply
              </button>

              <button
                style={{ ...styles.button, ...styles.clearButton }}
                onClick={handleClear}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateFilterDashboard;
